// Not ported (dead in the Angular app): getMappingSuggestions
import { api } from '@/core/http/apiClient';
import { exportFile } from '@/core/http/helpers';
import type {
  AtbDrilldown,
  CreateJournalPayload,
  EnsureNominalFromChartPayload,
  EnsureNominalFromChartResponse,
  ExportOptions,
  ImportExceptionPage,
  ImportExceptionQuery,
  ImportSummaryRow,
  ImportValidationResult,
  JournalDetail,
  JournalNominalOption,
  JournalReversalOptions,
  JournalScreenPage,
  JournalScreenQuery,
  JournalTypeOption,
  MappingCheckReport,
  MappingOverrideInput,
  MappingWorkspacePage,
  MappingWorkspaceQuery,
  ReportingCodeNode,
  ResolveImportExceptionPayload,
  ResolveImportExceptionResponse,
  ResumeImportResponse,
  SaveJournalPayload,
  SaveMappingsOptions,
  SaveMappingsResponse,
  SetJournalReviewPayload,
  TrialBalanceVarianceQuery,
  TrialBalanceVarianceResponse,
} from '@/core/models/phase1.models';

const exportBlob = (path: string, body: unknown, isPrint: boolean) =>
  exportFile(path, body, isPrint, { unwrapBlobErrors: true });

export const phase1Service = {
  // ---- Import exceptions ----

  /** Import batches for the period, newest first (Accounts Production core endpoint). */
  getImports: (periodId: string) =>
    api.get<ImportSummaryRow[]>(`accounts-production/periods/${periodId}/trial-balance/imports`),

  getExceptions: (periodId: string, query: ImportExceptionQuery) =>
    api.post<ImportExceptionPage>(`ImportExceptions/getExceptions/${periodId}`, query),

  validateImport: (periodId: string, importBatchId: string) =>
    api.post<ImportValidationResult>(`ImportExceptions/validateImport/${periodId}/${importBatchId}`, {}),

  resolveException: (periodId: string, importExceptionId: string, payload: ResolveImportExceptionPayload) =>
    api.post<ResolveImportExceptionResponse>(
      `ImportExceptions/resolveException/${periodId}/${importExceptionId}`,
      payload,
    ),

  resumeImport: (periodId: string, importBatchId: string) =>
    api.post<ResumeImportResponse>(`ImportExceptions/resumeImport/${periodId}/${importBatchId}`, {}),

  // ---- Nominal mapping ----

  getMappingWorkspace: (periodId: string, query: MappingWorkspaceQuery) =>
    api.post<MappingWorkspacePage>(`NominalMapping/getWorkspace/${periodId}`, query),

  getReportingCodes: (periodId: string) => api.get<ReportingCodeNode[]>(`NominalMapping/getReportingCodes/${periodId}`),

  /**
   * APT335: `confirmWarnings` is sent after the user has seen the warnings of a first attempt (AP-MAP-005, a code on the other side of the account's
   * nature). A code of another section is refused (AP-MAP-003) and a system mapping is refused to everybody (AP-MAP-004: the client's answer of 28 Oct
   * 2026 was "Nobody", so there is no administrator override and no reason to send).
   */
  saveMappings: (periodId: string, mappings: MappingOverrideInput[], options?: SaveMappingsOptions) =>
    api.post<SaveMappingsResponse>(`NominalMapping/saveMappings/${periodId}`, {
      mappings,
      confirmWarnings: options?.confirmWarnings ?? false,
    }),

  /** APT335: the mapping check of a period - each nominal's mapping judged against its chart account (section, side, system mapping). */
  getMappingCheck: (periodId: string) => api.get<MappingCheckReport>(`ChartMapping/consistency/${periodId}`),

  applySuggestions: (periodId: string) =>
    api.post<SaveMappingsResponse>(`NominalMapping/applySuggestions/${periodId}`, {}),

  exportMappings: (periodId: string, query: MappingWorkspaceQuery, options: ExportOptions) =>
    exportBlob(`NominalMapping/exportMappings/${periodId}`, { ...query, ...options }, options.isPrint),

  // ---- Adjusted trial balance ----

  getVariance: (periodId: string, query: TrialBalanceVarianceQuery) =>
    api.post<TrialBalanceVarianceResponse>(`AdjustedTrialBalance/getVariance/${periodId}`, query),

  getDrilldown: (periodId: string, reportingCodeId: string) =>
    api.get<AtbDrilldown>(`AdjustedTrialBalance/getDrilldown/${periodId}/${reportingCodeId}`),

  exportVariance: (periodId: string, query: TrialBalanceVarianceQuery, options: ExportOptions) =>
    exportBlob(`AdjustedTrialBalance/exportVariance/${periodId}`, { ...query, ...options }, options.isPrint),

  // ---- Journals ----

  getJournalTypes: () => api.get<JournalTypeOption[]>('PeriodJournals/getJournalTypes'),

  getJournals: (periodId: string, query: JournalScreenQuery) =>
    api.post<JournalScreenPage>(`PeriodJournals/getJournals/${periodId}`, query),

  getJournal: (periodId: string, journalId: string) =>
    api.get<JournalDetail>(`PeriodJournals/getJournal/${periodId}/${journalId}`),

  getJournalNominals: (periodId: string, search?: string) => {
    // Kept in the path (not params) so the search is encoded with encodeURIComponent, like Angular.
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    return api.get<JournalNominalOption[]>(`PeriodJournals/getNominals/${periodId}${query}`);
  },

  createJournal: (periodId: string, payload: CreateJournalPayload) =>
    api.post<JournalDetail>(`PeriodJournals/createJournal/${periodId}`, payload),

  saveJournal: (periodId: string, journalId: string, payload: SaveJournalPayload) =>
    api.post<JournalDetail>(`PeriodJournals/saveJournal/${periodId}/${journalId}`, payload),

  postJournal: (periodId: string, journalId: string, expectedETag: string | null, comment?: string) =>
    api.post<unknown>(`PeriodJournals/postJournal/${periodId}/${journalId}`, {
      expectedETag,
      comment: comment ?? null,
    }),

  /**
   * APT336: `targetPeriodId` (the NEXT accounting period, from getReversalOptions) posts the reversal there; leave it out for the journal's own period.
   * `confirmFinalisedReversal` is sent only after the user has approved the confirmation shown for a FINALISED period's journal (the API asks again
   * until it is true, AP-JRN-009).
   */
  reverseJournal: (
    periodId: string,
    journalId: string,
    reversalDate: string,
    narrative: string | null,
    expectedETag: string | null,
    targetPeriodId?: string | null,
    confirmFinalisedReversal?: boolean,
  ) =>
    api.post<unknown>(`PeriodJournals/reverseJournal/${periodId}/${journalId}`, {
      reversalDate,
      narrative,
      expectedETag,
      ...(targetPeriodId ? { targetPeriodId } : {}),
      ...(confirmFinalisedReversal ? { confirmFinalisedReversal: true } : {}),
    }),

  /** APT336: where a posted journal of the period can be reversed - its own period, and the next one when there is one. */
  getReversalOptions: (periodId: string) =>
    api.get<JournalReversalOptions>(`PeriodJournals/getReversalOptions/${periodId}`),

  deleteJournal: (periodId: string, journalId: string) =>
    api.post<boolean>(`PeriodJournals/deleteJournal/${periodId}/${journalId}`, {}),

  setJournalReview: (periodId: string, journalId: string, payload: SetJournalReviewPayload) =>
    api.post<unknown>(`PeriodJournals/setReviewStatus/${periodId}/${journalId}`, payload),

  exportJournals: (periodId: string, query: JournalScreenQuery, options: ExportOptions) =>
    exportBlob(`PeriodJournals/exportJournals/${periodId}`, { ...query, ...options }, options.isPrint),

  // Adds one of the client's chart accounts to the period with a nil source balance (journal nominal picker).
  ensureNominalFromChart: (periodId: string, payload: EnsureNominalFromChartPayload) =>
    api.post<EnsureNominalFromChartResponse>(`accounts-production/periods/${periodId}/nominals/from-chart`, payload),
};
